import cn from 'classnames';
import { ChangeEvent, FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Button } from 'src/components/common/Button';
import { Input } from 'src/components/common/Input';
import { SvgIcon } from 'src/components/common/SvgIcon';
import { icons } from 'src/configs/icons';
import { setBodyOverflow } from 'src/helpers/utils';
import { useConnectedAction } from 'src/hooks/use-connected-action';
import { useDebounce } from 'src/hooks/use-debounce';
import { MediaManager } from 'src/manager/MediaManager';
import { setSelectedTag } from 'src/store/games/actions';
import { toggleMobileProvider } from 'src/store/mobile/actions';
import { RootState } from 'src/types/store-types';
import { Logo } from '../../Logo';
import MultiSelectProvidersList from './components/MultiSelectProvidersList';
import _styles from './styles.module.scss';
import ViewWithoutResult from './ViewWithoutResult';

const MobileProviders: FC = () => {
  const { t }: Translation = useTranslation();
  const providerModalRef: any = useRef();

  const _setSelectedTag = useConnectedAction(setSelectedTag);
  const _toggleMobileProvider = useConnectedAction(toggleMobileProvider);

  const { selectedCategory } = useSelector((state: RootState) => state.app);
  const { selectedProvider } = useSelector((state: RootState) => state.games);
  const { categoriesData } = useSelector((state: RootState) => state.configs);

  const [searchVal, setSearchVal] = useState<string>('');
  const [providers, setProviders] = useState<string[]>([]);
  const [selectedProvidersList, setSelectedProvidersList] = useState<string[]>(selectedProvider);

  const debouncedSearchTerm: any = useDebounce(searchVal, 500);

  const toggleAllProviders = (): void => {
    setSelectedProvidersList(selectedProvidersList.includes('all_providers') ? [] : ['all_providers']);
  };

  const removeProvider = (provider: string): void => {
    const index = selectedProvidersList.indexOf(provider);
    if (index !== -1) {
      selectedProvidersList.splice(index, 1);
    }
  };

  const addProvider = (provider: string): void => {
    setSelectedProvidersList([...selectedProvidersList, provider]);
  };

  const selectMultiProviders = (provider: string) => () => {
    localStorage.getItem('selectedTag') === 'home' && _setSelectedTag('allGames');
    if (provider === 'all_providers') {
      toggleAllProviders();
    } else {
      if (selectedProvidersList.includes('all_providers')) {
        removeProvider('all_providers');
      }
      if (selectedProvidersList.includes(provider)) {
        const filteredProviderList = selectedProvidersList.filter((item) => item !== provider);
        if (!filteredProviderList.length) {
          filteredProviderList.push('all_providers');
        }

        setSelectedProvidersList(filteredProviderList);
      } else {
        addProvider(provider);
      }
    }
  };

  const onChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const { value } = e.target;

    setSearchVal(value);

    if (categoriesData) {
      const foundedProviders = categoriesData[selectedCategory]?.providers?.filter((f) =>
        f.toLocaleLowerCase()?.trim().includes(value.toLocaleLowerCase()?.trim())
      );

      setProviders(foundedProviders);
    }
  };

  const onSearchInputValueRemove = (): void => {
    setSearchVal('');
    if (categoriesData) {
      setProviders(categoriesData[selectedCategory]?.providers);
    }
  };

  const closeProvidersFIlter = (): void => {
    setBodyOverflow('set');
    _toggleMobileProvider();
  };

  const content = useMemo(() => {
    if (providers?.length) {
      return (
        <MultiSelectProvidersList
          providers={providers}
          selectProvider={selectMultiProviders}
          selectedProvidersList={selectedProvidersList}
        />
      );
    }

    return <ViewWithoutResult />;
  }, [providers, debouncedSearchTerm, selectedProvidersList]);

  useEffect(() => {
    if (categoriesData && selectedCategory) {
      const categoryData = categoriesData[selectedCategory];

      setProviders(categoryData?.providers);
    } else {
      setProviders(['all_providers']);
    }
  }, [categoriesData, selectedCategory]);

  useEffect(() => {
    document.body.setAttribute('style', 'overflow: hidden');
    return () => document.body.removeAttribute('style');
  }, []);

  return (
    <div className={_styles.provider_container} ref={providerModalRef}>
      <div>
        <div className={_styles.logo}>
          <Logo settings={{ src: 'logo.png' }} />
        </div>
        <div className={_styles.menu_icon_wrapper}>
          <p className={_styles.title}>{t('providers')}</p>
          <Button type="button" className={_styles.close_menu_wrapper} onClick={closeProvidersFIlter}>
            <SvgIcon src={icons.close} />
          </Button>
        </div>
      </div>
      <div className={_styles.content_wrapper}>
        <div className={cn(_styles.search_input, 'mobile_search_input')}>
          <Input onChange={onChange} value={searchVal} name={''} placeholder={t('search')} />
          <SvgIcon src={icons.close} className={_styles.input_close_icon} onClick={onSearchInputValueRemove} />
          <div className={cn(_styles.search_icon, 'mobile_search_icon')}>
            <SvgIcon src={MediaManager.getSrcFromMediaAssets('search.svg', '/icons/svg/')} />
          </div>
        </div>
        {content}
      </div>
    </div>
  );
};
export default MobileProviders;
